import { create } from 'zustand'
import type { OrderStatus } from '../model/types'
import type { AddressData, PaymentMethod } from '@/modules/checkout'
import type { CartItem } from '@/modules/cart'
import { ordersApi, type OrderStatus as ApiOrderStatus, type Order as ApiOrder } from '@/modules/orders-api'

export interface AdminOrderFull {
  id: string            // DB id (uuid) — key chính, để PATCH/GET theo id
  code: string          // mã đơn hiển thị (VD XD261104...)
  createdAt: string
  address: AddressData
  payment: PaymentMethod
  items: CartItem[]
  subtotal: number
  discount: number
  shippingFee: number
  grandTotal: number
  status: OrderStatus
  statusHistory: { status: OrderStatus; at: string; note?: string }[]
  note?: string
}

// --- Mapper 2 chiều ---
const API_TO_UI_STATUS: Record<ApiOrderStatus, OrderStatus> = {
  PENDING:   'pending',
  CONFIRMED: 'confirmed',
  SHIPPING:  'shipping',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
}
const UI_TO_API_STATUS: Record<OrderStatus, ApiOrderStatus> = {
  pending:   'PENDING',
  confirmed: 'CONFIRMED',
  shipping:  'SHIPPING',
  delivered: 'DELIVERED',
  cancelled: 'CANCELLED',
}

function toFull(o: ApiOrder): AdminOrderFull {
  return {
    id: o.id,
    code: o.code ?? o.id,
    createdAt: o.createdAt,
    address: {
      fullName: o.address.fullName,
      phone: o.address.phone,
      email: o.address.email,
      province: o.address.province,
      district: o.address.district,
      ward: o.address.ward,
      street: o.address.street,
      note: o.address.note ?? '',
    },
    payment: o.payment === 'COD' ? 'cod' : 'qr',
    items: o.items.map((it) => ({
      id: it.productId,
      slug: it.slug,
      name: it.name,
      brand: it.brand,
      image: it.image,
      price: it.price,
      oldPrice: it.oldPrice,
      qty: it.qty,
    })),
    subtotal: o.subtotal,
    discount: o.discount,
    shippingFee: o.shippingFee,
    grandTotal: o.grandTotal,
    status: API_TO_UI_STATUS[o.status],
    statusHistory: (o.statusHistory ?? []).map((h) => ({
      status: API_TO_UI_STATUS[h.status],
      at: h.at,
      note: h.note ?? undefined,
    })),
    note: '',
  }
}

interface OrdersState {
  orders: AdminOrderFull[]
  loading: boolean
  error: string | null

  fetchAll: () => Promise<void>
  fetchOne: (id: string) => Promise<AdminOrderFull | undefined>
  getById: (id: string) => AdminOrderFull | undefined
  getByCode: (code: string) => AdminOrderFull | undefined

  updateStatus: (id: string, status: OrderStatus, note?: string) => Promise<void>
  addNote: (id: string, note: string) => void
  remove: (id: string) => Promise<void>
  reset: () => void

  // WebSocket handlers (bước 10 sẽ wire)
  _upsert: (o: AdminOrderFull) => void
}

export const useAdminOrdersStore = create<OrdersState>()((set, get) => ({
  orders: [],
  loading: false,
  error: null,

  fetchAll: async () => {
    set({ loading: true, error: null })
    try {
      const page = await ordersApi.adminList({ page: 1, pageSize: 100 })
      set({ orders: page.items.map(toFull), loading: false })
    } catch (err: any) {
      set({ loading: false, error: err?.message ?? 'Không tải được danh sách đơn' })
    }
  },

  fetchOne: async (id) => {
    try {
      const o = await ordersApi.adminOne(id)
      const full = toFull(o)
      get()._upsert(full)
      return full
    } catch {
      return undefined
    }
  },

  getById:   (id)   => get().orders.find((o) => o.id === id || o.code === id),
  getByCode: (code) => get().orders.find((o) => o.code === code),

  updateStatus: async (id, status, note) => {
    try {
      const o = await ordersApi.adminStatus(id, { status: UI_TO_API_STATUS[status], note })
      get()._upsert(toFull(o))
    } catch (err: any) {
      set({ error: err?.message ?? 'Cập nhật trạng thái thất bại' })
      throw err
    }
  },

  // Backend chưa có endpoint ghi chú nội bộ → lưu local trong store (reset khi F5)
  addNote: (id, note) => set({
    orders: get().orders.map((o) => (o.id === id || o.code === id ? { ...o, note } : o)),
  }),

  // Backend chưa có endpoint delete đơn → chỉ remove khỏi local cache
  remove: async (id) => {
    set({ orders: get().orders.filter((o) => o.id !== id && o.code !== id) })
  },

  reset: () => set({ orders: [], error: null }),

  _upsert: (o) => set({
    orders: (() => {
      const idx = get().orders.findIndex((x) => x.id === o.id)
      if (idx < 0) return [o, ...get().orders]
      const next = get().orders.slice()
      next[idx] = o
      return next
    })(),
  }),
}))
